import React, { useEffect, useState } from 'react';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';
import startupService from '../../services/startupService';

export default function TeamTab({ startupId }) {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [openDialog, setOpenDialog] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        role: '',
        course: '',
        semester: '',
        year: '',
        background: '',
    });

    useEffect(() => {
        fetchMembers();
    }, [startupId]);

    const fetchMembers = async () => {
        try {
            setLoading(true);
            const data = await startupService.getTeamMembers(startupId);
            setMembers(data);
        } catch (err) {
            console.error('Error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddClick = () => {
        setEditingId(null);
        setFormData({
            name: '',
            email: '',
            phone: '',
            role: '',
            course: '',
            semester: '',
            year: '',
            background: '',
        });
        setOpenDialog(true);
    };

    const handleEditClick = (member) => {
        setEditingId(member.id);
        setFormData(member);
        setOpenDialog(true);
    };

    const handleSave = async () => {
        try {
            if (!formData.name.trim()) {
                setError('Member name is required');
                return;
            }

            setLoading(true);

            if (editingId) {
                await startupService.updateTeamMember(editingId, formData);
                setMembers(members.map(m => m.id === editingId ? { id: editingId, ...formData } : m));
            } else {
                const newMember = await startupService.addTeamMember(startupId, formData);
                setMembers([...members, newMember]);
            }

            setOpenDialog(false);
            setError('');
        } catch (err) {
            console.error('Error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (memberId) => {
        if (!window.confirm('Are you sure you want to delete this team member?')) return;

        try {
            setLoading(true);
            await startupService.deleteTeamMember(startupId, memberId);
            setMembers(members.filter(m => m.id !== memberId));
        } catch (err) {
            console.error('Error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    return (
        <div className="space-y-4">
            {error && (
                <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg flex justify-between">
                    <span>{error}</span>
                    <button onClick={() => setError('')} className="font-bold">×</button>
                </div>
            )}

            <button
                onClick={handleAddClick}
                className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold"
            >
                <AddIcon /> Add Team Member
            </button>

            {loading && members.length === 0 ? (
                <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
            ) : members.length === 0 ? (
                <div className="p-8 text-center bg-gray-50 rounded-lg text-gray-500">
                    No team members yet. Add one to get started!
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full bg-white rounded-lg shadow">
                        <thead className="bg-gray-100 border-b-2 border-gray-300">
                            <tr>
                                <th className="px-6 py-4 text-left font-semibold text-gray-700">Name</th>
                                <th className="px-6 py-4 text-left font-semibold text-gray-700">Email</th>
                                <th className="px-6 py-4 text-left font-semibold text-gray-700">Phone</th>
                                <th className="px-6 py-4 text-left font-semibold text-gray-700">Role</th>
                                <th className="px-6 py-4 text-left font-semibold text-gray-700">Course</th>
                                <th className="px-6 py-4 text-left font-semibold text-gray-700">Semester</th>
                                <th className="px-6 py-4 text-center font-semibold text-gray-700">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map((member, idx) => (
                                <tr key={member.id} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 transition`}>
                                    <td className="px-6 py-4 font-medium text-gray-900">{member.name}</td>
                                    <td className="px-6 py-4 text-gray-700">{member.email}</td>
                                    <td className="px-6 py-4 text-gray-700">{member.phone}</td>
                                    <td className="px-6 py-4 text-gray-700">{member.role}</td>
                                    <td className="px-6 py-4 text-gray-700">{member.course}</td>
                                    <td className="px-6 py-4 text-gray-700">{member.semester}</td>
                                    <td className="px-6 py-4 text-center">
                                        <div className="flex justify-center gap-3">
                                            <button
                                                onClick={() => handleEditClick(member)}
                                                className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition"
                                            >
                                                <EditIcon className="text-xl" />
                                            </button>
                                            <button
                                                onClick={() => handleDelete(member.id)}
                                                className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition"
                                            >
                                                <DeleteIcon className="text-xl" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add/Edit Dialog */}
            {openDialog && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-2xl max-w-md w-full p-8">
                        <h2 className="text-2xl font-bold text-gray-900 mb-6">
                            {editingId ? 'Edit Team Member' : 'Add Team Member'}
                        </h2>

                        <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Phone</label>
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Role</label>
                                <input
                                    type="text"
                                    name="role"
                                    value={formData.role}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Course</label>
                                <input
                                    type="text"
                                    name="course"
                                    value={formData.course}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Semester</label>
                                <input
                                    type="text"
                                    name="semester"
                                    value={formData.semester}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Year</label>
                                <input
                                    type="text"
                                    name="year"
                                    value={formData.year}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Background</label>
                                <textarea
                                    name="background"
                                    value={formData.background}
                                    onChange={handleChange}
                                    rows="2"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                ></textarea>
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <button
                                onClick={() => setOpenDialog(false)}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition font-semibold"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={loading || !formData.name}
                                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-semibold disabled:opacity-50"
                            >
                                {loading ? '⏳ Saving...' : '✓ Save'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
